import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CellProps, Column } from "react-table";
import { Text, Center, Flex, Tooltip } from "@chakra-ui/react";
import { CheckIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import { primaryColor } from "../../../utils/colors";
import TableComponent from "../../../components/organism/Table/TableComponent";
import { tableInput } from "../../../utils/styles/tableStyles";
import NumberInputFieldComponent from "../../../components/molecule/NumberInputField/NumberInputFieldComponent";
import { Control, FieldValues, UseFormGetValues } from "react-hook-form";
import { INDICATOR_URL } from "../../../utils/endpoints";
import api from "../../../utils/api";
import { Toast } from "../../../components/atom/Toast/Toast";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import { BIGGER_FIELD } from "../../../utils/constants";
import { validateAlertState } from "./validateAlertState";
import { AuthUser } from "../../../types/User/AuthUser";

interface TableIndicatorAlertProps {
  dataTable: any[];
  originalDataTable: any[];
  isLoaded: boolean;
  setValue: (key: string, value: any) => void;
  getValues: UseFormGetValues<FieldValues>;
  control: Control<any>;
  setIsSearching: Dispatch<SetStateAction<boolean>>;
  user: AuthUser;
}

const TableIndicatorAlert: FC<TableIndicatorAlertProps> = ({
  dataTable,
  originalDataTable,
  isLoaded,
  setValue,
  getValues,
  control,
  setIsSearching,
  user,
}) => {
  const [inputErrors, setInputErrors] = useState<{ [key: string]: string }>({});
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<any[]>(dataTable);
  const [originalRowData, setOriginalRowData] = useState<any | null>(null);
  const { addToast } = Toast();

  useEffect(() => {
    setEditingRowId(null);
    setEditingRowIndex(null);
    setEditedData(dataTable);
  }, [dataTable]);

  useEffect(() => {
    if (originalDataTable) {
      setEditedData(originalDataTable);
      originalDataTable.forEach((row) => {
        Object.keys(row).forEach((key) => {
          setValue(`dataTable[${row.id}].${key}`, row[key]);
        });
      });
    }
  }, [originalDataTable, setValue]);

  const handleEditClick = (rowId: number, rowIndex: number) => {
    if (editingRowId !== null && editingRowId !== rowId) {
      handleCancelClick(editingRowId);
    }
    setEditingRowId(rowId);
    const currentRow = editedData.find((row) => row.id === rowId);
    setOriginalRowData({ ...currentRow });

    setEditingRowIndex(rowIndex);
  };

  const handleSaveClick = async (rowId: number) => {
    try {
      const data = getValues(`dataTable[${rowId}]`);
      const errors = validateAlertState(data.alertState);

      if (Object.keys(errors).length > 0) {
        setInputErrors(errors);
        addToast({
          status: "error",
          title: "Erro",
          description: BIGGER_FIELD,
        });
        return;
      }

      setInputErrors({});

      const considerVariation = [
        data.alertState.maxVariationValue,
        data.alertState.minPeriodVariation,
        data.alertState.maxPeriodVariation,
        data.alertState.minYearVariation,
        data.alertState.maxYearVariation,
      ].some((value) => value !== null || value !== undefined);

      const reference = (() => {
        const healthySymbolId = data.healthyLevelOption
          ? data.healthyLevelOption.id
          : null;
        const unhealthySymbolId = data.unhealthyLevelOption
          ? data.unhealthyLevelOption.id
          : null;

        if (healthySymbolId === null && unhealthySymbolId === null) {
          return null;
        }

        return {
          healthySymbolId,
          healthyLesserValue: data.healthyIntervalFrom
            ? data.healthyIntervalFrom
            : null,
          healthyGreaterValue: data.healthyIntervalTo
            ? data.healthyIntervalTo
            : null,
          unhealthySymbolId,
          unhealthyLesserValue: data.unhealthyIntervalFrom
            ? data.unhealthyIntervalFrom
            : null,
          unhealthyGreaterValue: data.unhealthyIntervalTo
            ? data.unhealthyIntervalTo
            : null,
        };
      })();
      const bodyRequest = {
        id: data.id,
        name: data.name,
        typeId: data.type.id,
        subSystemId: data.subSystem.id,
        frequencyId: data.frequency.id,
        readingVariationId: data.readingVariation.id,
        levelId: data.level.id,
        alertState: {
          lowestValue: data.alertState.lowestValue
            ? data.alertState.lowestValue
            : null,
          biggestValue: data.alertState.biggestValue
            ? data.alertState.biggestValue
            : null,
          considerVariation: considerVariation,
          maxVariationValue: data.alertState.maxVariationValue
            ? data.alertState.maxVariationValue
            : null,
          minPeriodVariation: data.alertState.minPeriodVariation
            ? data.alertState.minPeriodVariation
            : null,
          maxPeriodVariation: data.alertState.maxPeriodVariation
            ? data.alertState.maxPeriodVariation
            : null,
          minYearVariation: data.alertState.minYearVariation
            ? data.alertState.minYearVariation
            : null,
          maxYearVariation: data.alertState.maxYearVariation
            ? data.alertState.maxYearVariation
            : null,
        },
        reference,
        metricId: data.metric.id,
        anotherMetric: data.anotherMetric ? data.anotherMetric : "",
        partnerId: data.partner.id,
        indicatorCollectionTypeId: data.collectionType.id,
      };

      const success = await handleSubmitItem(
        () => api.put(INDICATOR_URL, bodyRequest),
        addToast,
      );

      setEditingRowId(null);
      setEditingRowIndex(null);

      if (success) {
        setEditedData((prevData) =>
          prevData.map((row) =>
            row.id === rowId
              ? {
                  ...row,
                  ...bodyRequest,
                  alertState: { ...row.alertState, ...data.alertState },
                }
              : row,
          ),
        );
        setIsSearching(true);
        setEditingRowId(null);
      } else {
        const newData = [...editedData];
        const rowIndex = newData.findIndex((row) => row.id === rowId);
        newData[rowIndex] = originalRowData;
        setEditedData(newData);
      }
    } catch (error) {
      console.error("Error editing data:", error);
      addToast({
        status: "error",
        title: "Erro",
        description: `Ocorreu um erro ao alterar o item`,
      });
    }
  };

  const handleCancelClick = (rowId?: number) => {
    const idToRevert = rowId !== undefined ? rowId : editingRowId;

    if (idToRevert !== null && originalRowData) {
      Object.keys(originalRowData).forEach((key) => {
        setValue(`dataTable[${idToRevert}].${key}`, originalRowData[key]);
      });

      const newData = [...editedData];
      const rowIndex = newData.findIndex((row) => row.id === idToRevert);

      if (rowIndex !== -1) {
        newData[rowIndex] = originalRowData;
        setEditedData(newData);
      }
    }
    setEditingRowId(null);
    setEditingRowIndex(null);
  };

  const handleInputChange = (rowId: number, columnId: string, value: any) => {
    setInputErrors({});
    const newData = [...editedData];
    const rowIndex = newData.findIndex((row) => row.id === rowId);
    if (newData[rowIndex]) {
      newData[rowIndex] = {
        ...newData[rowIndex],
        alertState: {
          ...newData[rowIndex].alertState,
          [columnId]: value,
        },
      };
    }
    setEditedData(newData);
  };

  useEffect(() => {
    setEditedData(dataTable);
  }, [dataTable]);

  const columnsStyle = [
    "alertState.lowestValue",
    "alertState.biggestValue",
    "alertState.minPeriodVariation",
    "alertState.maxPeriodVariation",
    "alertState.minYearVariation",
    "alertState.maxYearVariation",
  ];

  const columns = useMemo(() => {
    const baseColumns: Column[] = [
      {
        Header: "Indicador",
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: "Métrica",
        accessor: "metric.name",
        disableSortBy: true,
      },
      {
        Header: "Em Alerta se valor absoluto",
        disableSortBy: true,
        columns: [
          {
            Header: "Abaixo de:",
            disableSortBy: true,
            accessor: "alertState.lowestValue",
            Cell: ({ row, value }: CellProps<any, string>) =>
              editingRowId === row.original.id ? (
                <NumberInputFieldComponent
                  control={control}
                  name={`dataTable[${row.original.id}].alertState.lowestValue`}
                  onChange={(inputValue) =>
                    handleInputChange(
                      row.original.id,
                      "lowestValue",
                      inputValue,
                    )
                  }
                  isRequired={true}
                  isDisabled={false}
                  additionalStyle={tableInput}
                />
              ) : (
                <Text>{value}</Text>
              ),
          },
          {
            Header: "Acima de:",
            disableSortBy: true,
            accessor: "alertState.biggestValue",
            Cell: ({ row, value }: CellProps<any, string>) =>
              editingRowId === row.original.id ? (
                <NumberInputFieldComponent
                  control={control}
                  name={`dataTable[${row.original.id}].alertState.biggestValue`}
                  onChange={(inputValue) =>
                    handleInputChange(
                      row.original.id,
                      "biggestValue",
                      inputValue,
                    )
                  }
                  isRequired={true}
                  isDisabled={false}
                  additionalStyle={tableInput}
                  errors={inputErrors}
                />
              ) : (
                <Text>{value}</Text>
              ),
          },
        ],
      },
      {
        Header: () => (
          <Flex maxW="40">
            <Text>Considerar variações se valor absoluto superior a:</Text>
          </Flex>
        ),
        disableSortBy: true,
        accessor: "alertState.maxVariationValue",
        Cell: ({ row, value }: CellProps<any, string>) =>
          editingRowId === row.original.id ? (
            <NumberInputFieldComponent
              control={control}
              name={`dataTable[${row.original.id}].alertState.maxVariationValue`}
              onChange={(inputValue) =>
                handleInputChange(
                  row.original.id,
                  "maxVariationValue",
                  inputValue,
                )
              }
              isRequired={true}
              isDisabled={false}
              additionalStyle={tableInput}
            />
          ) : (
            <Text>{value}</Text>
          ),
      },
      {
        Header: "Variação do período (%)",
        disableSortBy: true,
        columns: [
          {
            Header: "Negativas em mais de:",
            disableSortBy: true,
            accessor: "alertState.minPeriodVariation",
            Cell: ({ row, value }: CellProps<any, string>) => {
              const metricName = row.original.metric?.name;
              const displayValue =
                value != null
                  ? metricName === "%"
                    ? `${value} p.p.`
                    : `${value}%`
                  : "";

              return editingRowId === row.original.id ? (
                <NumberInputFieldComponent
                  control={control}
                  name={`dataTable[${row.original.id}].alertState.minPeriodVariation`}
                  onChange={(inputValue) =>
                    handleInputChange(
                      row.original.id,
                      "minPeriodVariation",
                      inputValue,
                    )
                  }
                  isRequired={true}
                  isDisabled={false}
                  additionalStyle={tableInput}
                />
              ) : (
                <Text>{displayValue}</Text>
              );
            },
          },
          {
            Header: "Positivas em mais de:",
            disableSortBy: true,
            accessor: "alertState.maxPeriodVariation",
            Cell: ({ row, value }: CellProps<any, string>) => {
              const metricName = row.original.metric?.name;
              const displayValue =
                value != null
                  ? metricName === "%"
                    ? `${value} p.p.`
                    : `${value}%`
                  : "";

              return editingRowId === row.original.id ? (
                <NumberInputFieldComponent
                  control={control}
                  name={`dataTable[${row.original.id}].alertState.maxPeriodVariation`}
                  onChange={(inputValue) =>
                    handleInputChange(
                      row.original.id,
                      "maxPeriodVariation",
                      inputValue,
                    )
                  }
                  isRequired={true}
                  isDisabled={false}
                  additionalStyle={tableInput}
                />
              ) : (
                <Text>{displayValue}</Text>
              );
            },
          },
        ],
      },
      {
        Header: "Variação Anual (%)",
        disableSortBy: true,
        columns: [
          {
            Header: "Negativas em mais de:",
            disableSortBy: true,
            accessor: "alertState.minYearVariation",
            Cell: ({ row, value }: CellProps<any, string>) => {
              const metricName = row.original.metric?.name;
              const displayValue =
                value != null
                  ? metricName === "%"
                    ? `${value} p.p.`
                    : `${value}%`
                  : "";

              return editingRowId === row.original.id ? (
                <NumberInputFieldComponent
                  control={control}
                  name={`dataTable[${row.original.id}].alertState.minYearVariation`}
                  onChange={(inputValue) =>
                    handleInputChange(
                      row.original.id,
                      "minYearVariation",
                      inputValue,
                    )
                  }
                  isRequired={true}
                  isDisabled={false}
                  additionalStyle={tableInput}
                />
              ) : (
                <Text>{displayValue}</Text>
              );
            },
          },
          {
            Header: "Positivas em mais de:",
            disableSortBy: true,
            accessor: "alertState.maxYearVariation",
            Cell: ({ row, value }: CellProps<any, string>) => {
              const metricName = row.original.metric?.name;
              const displayValue =
                value != null
                  ? metricName === "%"
                    ? `${value} p.p.`
                    : `${value}%`
                  : "";

              return editingRowId === row.original.id ? (
                <NumberInputFieldComponent
                  control={control}
                  name={`dataTable[${row.original.id}].alertState.maxYearVariation`}
                  onChange={(inputValue) =>
                    handleInputChange(
                      row.original.id,
                      "maxYearVariation",
                      inputValue,
                    )
                  }
                  isRequired={true}
                  isDisabled={false}
                  additionalStyle={tableInput}
                />
              ) : (
                <Text>{displayValue}</Text>
              );
            },
          },
        ],
      },
    ];
    if (user?.permissions && user.permissions.includes("EditIndicators")) {
      baseColumns.push({
        Header: "Editar",
        accessor: "edit",
        disableSortBy: true,
        Cell: ({ row }: CellProps<any, string>) => (
          <Center justifyContent="flex-end">
            {editingRowId === row.original.id ? (
              <Flex alignItems="center" justifyContent="center">
                <Tooltip label="Guardar" aria-label="Guardar">
                  <CheckIcon
                    boxSize="22px"
                    mr="10px"
                    cursor="pointer"
                    color="green.300"
                    onClick={() => handleSaveClick(row.original.id)}
                  />
                </Tooltip>
                <Tooltip label="Descartar" aria-label="Descartar">
                  <CloseIcon
                    boxSize={4}
                    cursor="pointer"
                    color="red.300"
                    onClick={() => handleCancelClick()}
                  />
                </Tooltip>
              </Flex>
            ) : (
              <EditIcon
                color={primaryColor}
                boxSize={6}
                mr="10px"
                cursor="pointer"
                onClick={() => {
                  handleEditClick(row.original.id, row.index);
                }}
              />
            )}
          </Center>
        ),
      });
    }
    return baseColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRowId]);

  return (
    <TableComponent
      isLoaded={isLoaded}
      columns={columns}
      data={editedData}
      columnsStyle={columnsStyle}
      fixedColumnIds={[{ value: "edit", hasShadow: true }]}
      editingRowIndex={editingRowIndex}
    />
  );
};

export default TableIndicatorAlert;
